import { waehleAdresse } from './adresse';
import type { Ausgeblendet, DienstleisterRoh, EigentuemerRoh, Filter, Kandidat, KanalZeile, KontaktRoh, MieterRoh } from './typen';

/** Kanaele nach Besitzer buendeln — sonst filtert jede Zeile die ganze Liste erneut. */
function nachBesitzer(kanaele: KanalZeile[]): Map<string, KanalZeile[]> {
  const map = new Map<string, KanalZeile[]>();
  for (const k of kanaele || []) {
    const bisher = map.get(k.owner_id);
    if (bisher) bisher.push(k); else map.set(k.owner_id, [k]);
  }
  return map;
}

/** #579: heutiges Datum als YYYY-MM-DD (lokal) – Vergleichsbasis für Vertrags-/Mietende. */
export function heuteIso(jetzt: Date = new Date()): string {
  const m = String(jetzt.getMonth() + 1).padStart(2, '0');
  const t = String(jetzt.getDate()).padStart(2, '0');
  return `${jetzt.getFullYear()}-${m}-${t}`;
}

/** #579: Datum (YYYY-MM-DD…) liegt vor heute. Leer/unlesbar = nicht vorbei. */
export function istVorbei(datum: string | null | undefined, heute: string): boolean {
  const d = (datum || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) && d < heute;
}

/** #579: Objekt wird nicht mehr verwaltet. */
export function istArchiviert(objektStatus: string | null | undefined): boolean {
  return (objektStatus || '').trim().toLowerCase() === 'archiviert';
}

/** #579: Status-Text „aktiv“ in beliebiger Schreibweise; leer zählt bei Eigentümern/Kontakten als aktiv. */
function statusAktiv(status: string | null | undefined, leerIstAktiv: boolean): boolean {
  const s = (status || '').trim().toLowerCase();
  return s ? s === 'aktiv' : leerIstAktiv;
}

/** #579: Zahl der ausgeblendeten Kontakte, die nicht doch über eine andere Zuordnung dabei sind. */
export function zaehleAusgeblendet(ausgeblendet: Ausgeblendet, kandidaten: { owner_type: string; owner_id: string }[]): number {
  const dabei = new Set(kandidaten.map((k) => `${k.owner_type}:${k.owner_id}`));
  let n = 0;
  for (const key of ausgeblendet) if (!dabei.has(key)) n++;
  return n;
}

function passtZuFilter(objektId: string | null, einheitId: string | null, filter: Filter): boolean {
  if (filter.alle) return true;
  if (!objektId || !filter.objektIds.includes(objektId)) return false;
  // Leere Einheitenliste heisst „alle Einheiten des Objekts" — nicht „keine".
  const einheiten = filter.einheitIds || [];
  if (einheiten.length === 0) return true;
  return !!einheitId && einheiten.includes(einheitId);
}

/**
 * Aktive Mieter der gewaehlten Objekte (optional Einheiten) als Kandidaten.
 *
 * Nur status='aktiv': ein Rundschreiben an Ausgezogene waere peinlich und im Zweifel
 * ein Datenschutzproblem. #579: zusaetzlich raus, wenn das Mietende vorbei ist oder das
 * Objekt archiviert (nicht mehr verwaltet) ist. Kandidaten OHNE Adresse bleiben bewusst in der Liste —
 * sie werden nicht angeschrieben, landen aber auf der Druckliste fuer den Postweg
 * (im Bestand betrifft das 299 von 683 aktiven Mietern).
 */
export function ermittleMieterKandidaten(
  rohe: MieterRoh[], kanaele: KanalZeile[], filter: Filter, ausgeblendet: Ausgeblendet = new Set(), heute = heuteIso(),
): Kandidat[] {
  const kanalMap = nachBesitzer(kanaele);
  return (rohe || [])
    .filter((m) => passtZuFilter(m.objekt_id, m.einheit_id, filter))
    .filter((m) => {
      const aktiv = statusAktiv(m.status, false) && !istVorbei(m.mietende, heute) && !istArchiviert(m.objekt_status);
      if (!aktiv) ausgeblendet.add(`mieter:${m.id}`);
      return aktiv;
    })
    .map((m) => ({
      owner_type: 'mieter' as const,
      owner_id: m.id,
      anrede: m.anrede ?? null,
      nachname: m.nachname,
      name: [m.vorname, m.nachname].filter(Boolean).join(' ').trim() || 'Ohne Namen',
      email: waehleAdresse(kanalMap.get(m.id) || [], m.email),
      objekt_id: m.objekt_id,
      objekt_bezeichnung: m.objekt_bezeichnung || '',
      einheit_id: m.einheit_id,
      einheit_bezeichnung: m.einheit_bezeichnung || '',
      // Ein Mieter hat KEINE eigene Anschrift in der Datenbank — seine Postadresse ist
      // die des Objekts, in dem er wohnt. Bitte nicht auf `m.strasse` „zurueckkorrigieren":
      // die Spalte gibt es an `mieter` nicht, die Abfrage antwortet dann mit HTTP 400.
      strasse: m.objekt_strasse || '',
      hausnummer: m.objekt_hausnummer || '',
      plz: m.objekt_plz || '',
      stadt: m.objekt_stadt || '',
    }));
}

/**
 * Eigentuemer der gewaehlten Objekte. Die Rohdaten kommen aus objekt_eigentuemer
 * (die EINE Wahrheit fuer Eigentum, siehe Commit 7156eaa) und enthalten denselben
 * Eigentuemer mehrfach, wenn ihm mehrere gewaehlte Objekte gehoeren — er darf das
 * Rundschreiben trotzdem nur einmal bekommen.
 * #579: nur aktive – Status „inaktiv“, beendeter Verwaltervertrag (eigentuemer.vertragsende,
 * objekt_eigentuemer.vertrag_bis) oder archiviertes Objekt blenden die Zuordnung aus.
 */
export function ermittleEigentuemerKandidaten(
  rohe: EigentuemerRoh[], kanaele: KanalZeile[], filter: Filter, ausgeblendet: Ausgeblendet = new Set(), heute = heuteIso(),
): Kandidat[] {
  const kanalMap = nachBesitzer(kanaele);
  const gesehen = new Set<string>();
  const treffer: Kandidat[] = [];
  for (const e of rohe || []) {
    if (!filter.alle && !filter.objektIds.includes(e.objekt_id)) continue;
    if (gesehen.has(e.id)) continue;
    const aktiv = statusAktiv(e.status, true) && !istVorbei(e.vertragsende, heute)
      && !istVorbei(e.vertrag_bis, heute) && !istArchiviert(e.objekt_status);
    // Nicht in `gesehen` – eine weitere, aktive Zuordnung desselben Eigentümers zählt noch.
    if (!aktiv) { ausgeblendet.add(`eigentuemer:${e.id}`); continue; }
    gesehen.add(e.id);
    const istFirma = (e.anrede || '').toLowerCase() === 'firma';
    treffer.push({
      owner_type: 'eigentuemer',
      owner_id: e.id,
      anrede: istFirma ? null : e.anrede,
      nachname: istFirma ? null : e.nachname,
      name: (istFirma ? e.firma_name : [e.vorname, e.nachname].filter(Boolean).join(' ')) || 'Ohne Namen',
      email: waehleAdresse(kanalMap.get(e.id) || [], e.email),
      objekt_id: e.objekt_id,
      objekt_bezeichnung: e.objekt_bezeichnung || '',
      einheit_id: null,
      einheit_bezeichnung: '',
      // Ein Eigentuemer hat — anders als ein Mieter — seine eigene Anschrift.
      strasse: e.strasse || '',
      hausnummer: e.hausnummer || '',
      plz: e.plz || '',
      stadt: e.stadt || '',
    });
  }
  return treffer;
}

/**
 * #577: Dienstleister als Kandidaten. Nur aktive; derselbe Dienstleister an mehreren gewählten
 * Objekten bekommt die Mail einmal. Adresse wie bei Eigentümern aus Kanälen bzw. `email`.
 */
export function ermittleDienstleisterKandidaten(
  rohe: DienstleisterRoh[], kanaele: KanalZeile[], filter: Filter, ausgeblendet: Ausgeblendet = new Set(), heute = heuteIso(),
): Kandidat[] {
  const kanalMap = nachBesitzer(kanaele);
  const gesehen = new Set<string>();
  const treffer: Kandidat[] = [];
  for (const d of rohe || []) {
    if (!filter.alle && (!d.objekt_id || !filter.objektIds.includes(d.objekt_id))) continue;
    if (gesehen.has(d.id)) continue;
    // #579: inaktiver Dienstleister, beendete Objekt-Zuordnung oder archiviertes Objekt
    if (d.aktiv === false || istVorbei(d.bis, heute) || istArchiviert(d.objekt_status)) {
      ausgeblendet.add(`dienstleister:${d.id}`);
      continue;
    }
    gesehen.add(d.id);
    treffer.push({
      owner_type: 'dienstleister',
      owner_id: d.id,
      name: (d.name || '').trim() || 'Ohne Namen',
      email: waehleAdresse(kanalMap.get(d.id) || [], d.email),
      objekt_id: filter.alle ? null : d.objekt_id,
      objekt_bezeichnung: filter.alle ? '' : d.objekt_bezeichnung || '',
      einheit_id: null,
      einheit_bezeichnung: '',
      strasse: d.strasse || '',
      hausnummer: d.hausnummer || '',
      plz: d.plz || '',
      stadt: d.stadt || '',
    });
  }
  return treffer;
}

/** #577: sonstige Kontakte (Tabelle `kontakte`, nur Status aktiv). Sie haben keine Kanäle, nur `email`. */
export function ermittleKontaktKandidaten(
  rohe: KontaktRoh[], filter: Filter, ausgeblendet: Ausgeblendet = new Set(),
): Kandidat[] {
  const gesehen = new Set<string>();
  const treffer: Kandidat[] = [];
  for (const k of rohe || []) {
    if (!filter.alle && (!k.objekt_id || !filter.objektIds.includes(k.objekt_id))) continue;
    if (gesehen.has(k.id)) continue;
    if (!statusAktiv(k.status, true) || istArchiviert(k.objekt_status)) { ausgeblendet.add(`kontakt:${k.id}`); continue; }
    gesehen.add(k.id);
    const person = [k.vorname, k.nachname].filter(Boolean).join(' ').trim();
    const istFirma = (k.anrede || '').toLowerCase() === 'firma' || !person;
    treffer.push({
      owner_type: 'kontakt',
      owner_id: k.id,
      anrede: istFirma ? null : k.anrede,
      nachname: istFirma ? null : k.nachname,
      name: (istFirma ? (k.firma_name || person) : person) || 'Ohne Namen',
      email: waehleAdresse([], k.email),
      objekt_id: filter.alle ? null : k.objekt_id,
      objekt_bezeichnung: filter.alle ? '' : k.objekt_bezeichnung || '',
      einheit_id: null,
      einheit_bezeichnung: '',
      strasse: k.strasse || '',
      hausnummer: k.hausnummer || '',
      plz: k.plz || '',
      stadt: k.stadt || '',
    });
  }
  return treffer;
}
